// NotifyMenu — single component lifted onto every surface where the
// operator might want to send a member a notification. Drives
// orpc.notify.preview + orpc.notify.send.
//
// Shape:
//   - Trigger: a small bell-icon button.
//   - On click: opens a popover with channel chips (WhatsApp / Email / Voice),
//     an intent picker (when not pre-set), a preview pane fed by
//     notify.preview, and a Send button.
//   - On send: toast at bottom-right reports the per-channel outcome
//     ("Sent via WhatsApp · Email skipped (no email on file)"). Auto-
//     dismisses in 8s, same pattern as the existing voice_toast in dues.tsx.
//
// State is page-local. Multiple instances on the same page each carry
// their own toast — same as the voice-reminder button — so multiple
// rows can fire in parallel without colliding.

import { useEffect, useRef, useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { orpc } from '../lib/orpc.js';
import { friendly_error } from '../lib/friendly-error.js';
import { current_language } from '../i18n/index.js';
import { Icon } from './Icon.js';

type Channel = 'whatsapp' | 'email' | 'voice';
type Intent =
  | 'custom'
  | 'due_reminder'
  | 'overdue_alert'
  | 'partial_followup'
  | 'payment_receipt'
  | 'payout_scheduled'
  | 'payout_completed'
  | 'welcome'
  | 'year_end_statement';

interface NotifyContext {
  subscription_id?: string;
  chit_id?: string;
  payout_cycle_index?: number;
}

type BulkFilter =
  | {
      kind: 'dues';
      due_month: string;
      status?: Array<'overdue' | 'pending' | 'partial' | 'paid'>;
      chit_id?: string;
      search?: string;
    }
  | { kind: 'members'; status?: 'active' | 'watch' | 'defaulted' | 'archived'; search?: string }
  | { kind: 'chit_members'; chit_id: string }
  | { kind: 'ledger_cell'; chit_id: string; due_month: string };

interface SingleProps {
  subscriber_id: string;
  bulk?: undefined;
  /** Pre-selected default; operator can change to custom or another fit. */
  intent?: Intent;
  /** Extra ids handed to the dispatcher for templated rendering. */
  context?: NotifyContext;
  /** Visual variant — `compact` = tiny bell button (table rows / cell popovers),
   *  `inline` = a normal-sized button with the word "Notify" (page headers). */
  variant?: 'compact' | 'inline';
  /** Disabled — caller-controlled (e.g. when an ongoing operation should
   *  prevent the send button being clickable). Cooldowns and opt-outs are
   *  handled server-side by notify.send; this is only for local guards. */
  disabled?: boolean;
  /** Label shown next to the icon (only on `variant=inline`). Defaults to "Notify". */
  label?: string;
}

interface BulkProps {
  subscriber_id?: undefined;
  bulk: BulkFilter;
  /** Recipient-count estimate from the caller — drives the trigger label
   *  ("Notify N matching"). The server independently resolves the actual
   *  recipient list on send. */
  estimated_count: number;
  intent?: Intent;
  variant?: 'compact' | 'inline';
  disabled?: boolean;
  label?: string;
}

type Props = SingleProps | BulkProps;

// Intent cards drive the grid picker (icon + bilingual label). Order
// matters — `due_reminder` is the most-used default so it's the first
// card. Year-end statement and welcome sit later because they're rarely
// operator-triggered (welcome auto-fires; year-end is from Reports).
const INTENT_CARDS: Array<{ id: Intent; icon: string; en: string; ta: string }> = [
  { id: 'due_reminder', icon: '🔔', en: 'Due reminder', ta: 'நினைவூட்டல்' },
  { id: 'overdue_alert', icon: '⚠️', en: 'Overdue alert', ta: 'தாமதம்' },
  { id: 'partial_followup', icon: '½', en: 'Partial follow-up', ta: 'பகுதி' },
  { id: 'payment_receipt', icon: '✓', en: 'Receipt', ta: 'ரசீது' },
  { id: 'payout_scheduled', icon: '💸', en: 'Payout scheduled', ta: 'பணம் தயார்' },
  { id: 'payout_completed', icon: '✅', en: 'Payout done', ta: 'வழங்கப்பட்டது' },
  { id: 'welcome', icon: '👋', en: 'Welcome', ta: 'வரவேற்பு' },
  { id: 'year_end_statement', icon: '📄', en: 'Annual statement', ta: 'ஆண்டு அறிக்கை' },
  { id: 'custom', icon: '✏️', en: 'Custom message', ta: 'தனிப்பட்ட செய்தி' },
];

function intent_label(intent: Intent, lang: string): string {
  const card = INTENT_CARDS.find((c) => c.id === intent);
  if (!card) return intent;
  return lang === 'ta' ? card.ta : card.en;
}

// Channel meta — icons + bilingual labels for the channel rail.
const CHANNEL_META: Array<{
  id: Channel;
  icon: string;
  en: string;
  ta: string;
}> = [
  { id: 'whatsapp', icon: '📱', en: 'WhatsApp', ta: 'வாட்ஸ்அப்' },
  { id: 'email', icon: '📧', en: 'Email', ta: 'மின்னஞ்சல்' },
  { id: 'voice', icon: '🎙', en: 'Voice', ta: 'குரல்' },
];

interface Toast {
  ok: boolean;
  text: string;
}

export function NotifyMenu(props: Props): ReactElement {
  const lang = current_language();
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);
  // Helpers shared between header label + footer pluralisation.
  const il = (i: Intent): string => intent_label(i, lang);
  const is_bulk = props.bulk !== undefined;
  const trigger_ref = useRef<HTMLButtonElement>(null);
  const [open, set_open] = useState(false);

  // Close on Escape — standard modal affordance. Body scroll lock so the
  // page behind the modal doesn't drift while the operator is composing.
  useEffect(() => {
    if (!open) return;
    const on_key = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') set_open(false);
    };
    document.addEventListener('keydown', on_key);
    const prev_overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', on_key);
      document.body.style.overflow = prev_overflow;
    };
  }, [open]);
  const [intent, set_intent] = useState<Intent>(props.intent ?? (is_bulk ? 'due_reminder' : 'custom'));
  const [channels, set_channels] = useState<Channel[]>(['whatsapp']);
  const [custom_body, set_custom_body] = useState('');
  const [toast, set_toast] = useState<Toast | null>(null);

  // Preview is fetched whenever the popover is open + intent/context changes.
  // The proc is cheap (renders + opt-out filter; no I/O beyond Mongo reads).
  // Bulk mode skips the preview (recipient list is too varied; we just show
  // the count and intent label).
  const preview_q = useQuery({
    queryKey: [
      'notify-preview',
      is_bulk ? '__bulk__' : props.subscriber_id,
      intent,
      custom_body,
      is_bulk ? null : (props as SingleProps).context,
    ],
    queryFn: () => {
      const single = props as SingleProps;
      return orpc.notify.preview({
        subscriber_id: single.subscriber_id,
        intent,
        ...(single.context ? { context: single.context } : {}),
        ...(intent === 'custom' && custom_body ? { custom_body_text: custom_body } : {}),
      });
    },
    enabled: open && !is_bulk,
    staleTime: 0,
  });

  const send_m = useMutation({
    mutationFn: async () => {
      if (is_bulk) {
        const bulk = props as BulkProps;
        const bulk_key = `bulk.${bulk.bulk.kind}.${Date.now()}`;
        const r = await orpc.notify.send_bulk({
          filter: bulk.bulk,
          channels,
          intent,
          ...(intent === 'custom' && custom_body ? { custom_body_text: custom_body } : {}),
          bulk_key,
        });
        return { kind: 'bulk' as const, r };
      }
      const single = props as SingleProps;
      const r = await orpc.notify.send({
        subscriber_id: single.subscriber_id,
        intent,
        channels,
        ...(single.context ? { context: single.context } : {}),
        ...(intent === 'custom' && custom_body ? { custom_body_text: custom_body } : {}),
      });
      return { kind: 'single' as const, r };
    },
    onSuccess: (out) => {
      if (out.kind === 'bulk') {
        const s = out.r.summary;
        const text = t(
          `${s.sent} அனுப்பப்பட்டது · ${s.skipped} தவிர்க்கப்பட்டது · ${s.failed} தோல்வி`,
          `${s.sent} sent · ${s.skipped} skipped · ${s.failed} failed`,
        );
        set_toast({ ok: s.sent > 0, text });
      } else {
        const lines = out.r.attempted.map((a) => {
          if (a.status === 'sent' || a.status === 'queued') return `${channel_label(a.channel, lang)} ✓`;
          if (a.status === 'skipped') return `${channel_label(a.channel, lang)} — ${friendly_skip(a.skip_reason ?? '', lang)}`;
          return `${channel_label(a.channel, lang)} ✗ ${a.error ?? ''}`;
        });
        const any_ok = out.r.attempted.some((a) => a.status === 'sent' || a.status === 'queued');
        set_toast({ ok: any_ok, text: lines.join(' · ') });
      }
      set_open(false);
    },
    onError: (err) => {
      set_toast({ ok: false, text: friendly_error(err) });
    },
  });

  // Auto-dismiss the toast after 8 seconds — same cadence as the voice
  // reminder button so the operator's muscle memory carries over.
  useEffect(() => {
    if (!toast) return;
    const handle = setTimeout(() => set_toast(null), 8000);
    return () => clearTimeout(handle);
  }, [toast]);

  // Disable channels not present in the preview's skipped list — keeps the
  // operator from clicking Send on a channel that will be silently dropped.
  const skipped = preview_q.data?.skipped_channels ?? [];
  const skip_by_channel = new Map(skipped.map((s) => [s.channel, s.reason]));

  function toggle_channel(c: Channel): void {
    set_channels((cs) => (cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]));
  }

  return (
    <>
      <button
        ref={trigger_ref}
        type="button"
        className={'btn sm ' + (props.variant === 'inline' || is_bulk ? '' : 'ghost')}
        title={is_bulk ? t(`${(props as BulkProps).estimated_count} பேருக்கு அனுப்பு`, `Notify ${(props as BulkProps).estimated_count} matching`) : t('தகவல் அனுப்பு', 'Notify')}
        disabled={props.disabled || (is_bulk && (props as BulkProps).estimated_count === 0)}
        onClick={(e) => {
          e.stopPropagation();
          set_open((o) => !o);
        }}
      >
        <Icon.bell />
        {(props.variant === 'inline' || is_bulk) && (
          <span style={{ marginLeft: 6 }}>
            {is_bulk
              ? t(`${(props as BulkProps).estimated_count} பேருக்கு`, `Notify ${(props as BulkProps).estimated_count}`)
              : props.label ?? t('அனுப்பு', 'Notify')}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          className="notify-modal-backdrop"
          // Centered-modal scrim — dimmed, clickable to dismiss, ESC also
          // handled via the useEffect above. The modal sits centered both
          // axes; on mobile (narrow viewports) it stretches to viewport
          // width minus the padding.
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: 'rgba(28, 24, 20, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
          onClick={() => set_open(false)}
        >
          <div
            className="notify-modal"
            role="dialog"
            aria-modal="true"
            aria-label={is_bulk ? 'Send bulk notification' : 'Send notification'}
            style={{
              width: 520,
              maxWidth: '100%',
              maxHeight: 'calc(100vh - 48px)',
              background: 'var(--surface)',
              border: '1px solid var(--line)',
              borderRadius: 12,
              boxShadow: '0 24px 64px rgba(0, 0, 0, 0.32)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              animation: 'notify-modal-in 140ms ease-out',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* ─── Recipient header (single only) / bulk count header ─── */}
            {is_bulk ? (
              <div
                style={{
                  padding: '12px 14px',
                  borderBottom: '1px solid var(--line)',
                  background: 'var(--bg-elev)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: 'var(--primary)',
                    color: '#fff',
                    display: 'grid',
                    placeItems: 'center',
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  {(props as BulkProps).estimated_count}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>
                    {t(
                      `${(props as BulkProps).estimated_count} உறுப்பினர்களுக்கு`,
                      `${(props as BulkProps).estimated_count} members matching the filter`,
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    {t(
                      'ஒவ்வொருவருக்கும் opt-out / cooldown சர்வரில் சரிபார்க்கப்படும்',
                      'Server checks opt-out + cooldown per recipient.',
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <RecipientHeader
                subscriber={preview_q.data?.subscriber ?? null}
                loading={preview_q.isLoading}
                lang={lang}
              />
            )}

            {/* ─── Scrollable middle: intent grid + (custom textarea) + channels + previews ─── */}
            <div style={{ overflow: 'auto', padding: '12px 14px', flex: 1, minHeight: 0 }}>
              {/* Intent grid */}
              <SectionLabel>{t('நோக்கம்', 'Intent')}</SectionLabel>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 6,
                  marginBottom: 12,
                }}
              >
                {INTENT_CARDS.map((c) => {
                  const on = intent === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => set_intent(c.id)}
                      style={{
                        padding: '8px 6px',
                        border: '1px solid ' + (on ? 'var(--primary)' : 'var(--line)'),
                        borderRadius: 6,
                        background: on ? 'var(--primary-soft, var(--bg-elev))' : 'var(--surface)',
                        cursor: 'pointer',
                        textAlign: 'center',
                        fontSize: 11,
                        color: on ? 'var(--primary)' : 'var(--ink)',
                        fontWeight: on ? 600 : 400,
                        transition: 'all 80ms',
                      }}
                    >
                      <div style={{ fontSize: 16, lineHeight: 1, marginBottom: 3 }}>{c.icon}</div>
                      <div style={{ fontSize: 11 }}>{lang === 'ta' ? c.ta : c.en}</div>
                    </button>
                  );
                })}
              </div>

              {/* Custom body (only when intent === 'custom') */}
              {intent === 'custom' && (
                <div style={{ marginBottom: 12 }}>
                  <SectionLabel>{t('செய்தி', 'Message')}</SectionLabel>
                  <textarea
                    value={custom_body}
                    onChange={(e) => set_custom_body(e.target.value)}
                    rows={3}
                    placeholder={t('உங்கள் செய்தி இங்கே...', 'Type your message…')}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '8px 10px',
                      fontSize: 12,
                      border: '1px solid var(--line)',
                      borderRadius: 6,
                      background: 'var(--surface)',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>
              )}

              {/* Channel rail */}
              <SectionLabel>{t('அலைவரிசை', 'Channels')}</SectionLabel>
              <div style={{ marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {CHANNEL_META.map((m) => {
                  const skip_reason = skip_by_channel.get(m.id);
                  const is_skipped = !!skip_reason;
                  const selected = channels.includes(m.id);
                  const sub_text = is_skipped
                    ? friendly_skip(skip_reason, lang)
                    : m.id === 'whatsapp' && !is_bulk
                      ? preview_q.data?.wa_template_id ?? t('தனிப்பட்ட உரை', 'free-text')
                      : m.id === 'email' && !is_bulk
                        ? preview_q.data?.subscriber.email ?? ''
                        : m.id === 'voice'
                          ? t('TTS குரல்', 'TTS reminder')
                          : '';
                  return (
                    <label
                      key={m.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '8px 10px',
                        border: '1px solid ' + (selected && !is_skipped ? 'var(--primary)' : 'var(--line)'),
                        borderRadius: 6,
                        background: is_skipped ? 'var(--bg-elev)' : 'var(--surface)',
                        opacity: is_skipped ? 0.6 : 1,
                        cursor: is_skipped ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selected && !is_skipped}
                        disabled={is_skipped}
                        onChange={() => !is_skipped && toggle_channel(m.id)}
                      />
                      <span style={{ fontSize: 16 }}>{m.icon}</span>
                      <span style={{ fontSize: 13, fontWeight: 500, minWidth: 90 }}>
                        {lang === 'ta' ? m.ta : m.en}
                      </span>
                      <span
                        style={{
                          flex: 1,
                          fontSize: 11,
                          color: is_skipped ? 'var(--bad)' : 'var(--ink-3)',
                          textAlign: 'right',
                          fontFamily: m.id === 'whatsapp' && !is_skipped ? 'var(--font-mono, monospace)' : undefined,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {sub_text}
                      </span>
                    </label>
                  );
                })}
              </div>

              {/* Preview pane */}
              <SectionLabel>{t('முன்னோட்டம்', 'Preview')}</SectionLabel>
              {is_bulk ? (
                <BulkPreview
                  count={(props as BulkProps).estimated_count}
                  intent_label={il(intent)}
                  channels={channels}
                  lang={lang}
                />
              ) : preview_q.error ? (
                <PreviewError lang={lang} message={friendly_error(preview_q.error)} />
              ) : preview_q.isLoading ? (
                <PreviewLoading />
              ) : intent === 'custom' && !custom_body.trim() ? (
                <PreviewEmpty lang={lang} />
              ) : (
                <PreviewCards
                  channels={channels}
                  skip_by_channel={skip_by_channel}
                  data={preview_q.data}
                  lang={lang}
                />
              )}
            </div>

            {/* ─── Footer ─── */}
            {send_m.error && (
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--bad)',
                  background: 'var(--bad-soft)',
                  padding: '6px 14px',
                  borderTop: '1px solid var(--line)',
                }}
              >
                {friendly_error(send_m.error)}
              </div>
            )}
            <div
              style={{
                display: 'flex',
                gap: 8,
                justifyContent: 'flex-end',
                padding: '10px 14px',
                borderTop: '1px solid var(--line)',
                background: 'var(--bg-elev)',
              }}
            >
              <button type="button" className="btn sm ghost" onClick={() => set_open(false)}>
                {t('ரத்து', 'Cancel')}
              </button>
              <button
                type="button"
                className="btn sm primary"
                disabled={
                  send_m.isPending ||
                  channels.filter((c) => !skip_by_channel.has(c)).length === 0 ||
                  (intent === 'custom' && !custom_body.trim())
                }
                onClick={() => send_m.mutate()}
              >
                {send_m.isPending
                  ? '…'
                  : (() => {
                      const live = channels.filter((c) => !skip_by_channel.has(c));
                      if (live.length === 0) return t('அனுப்பு', 'Send');
                      const names = live
                        .map((c) => CHANNEL_META.find((m) => m.id === c))
                        .filter(Boolean)
                        .map((m) => (lang === 'ta' ? m!.ta : m!.en))
                        .join(' + ');
                      return t(`${names} அனுப்பு`, `Send via ${names}`);
                    })()}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            background: toast.ok ? 'var(--good)' : 'var(--bad-soft)',
            color: toast.ok ? 'white' : 'var(--bad)',
            padding: '10px 14px',
            borderRadius: 8,
            boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
            maxWidth: 380,
            fontSize: 12,
            zIndex: 100,
          }}
        >
          {toast.text}
        </div>
      )}
    </>
  );
}

function channel_label(c: Channel, lang: string): string {
  if (c === 'whatsapp') return 'WhatsApp';
  if (c === 'email') return lang === 'ta' ? 'மின்னஞ்சல்' : 'Email';
  return lang === 'ta' ? 'குரல்' : 'Voice';
}

function friendly_skip(reason: string, lang: string): string {
  const en: Record<string, string> = {
    tenant_disabled: 'Disabled in Settings · Notifications',
    opted_out: 'Member opted out',
    no_email_on_file: 'No email on file',
    no_subscription_context: 'No subscription context',
    no_template_for_intent: 'No template for this intent',
    idempotency_replay: 'Duplicate (already sent)',
    quota_exceeded: 'Daily quota reached',
  };
  const ta: Record<string, string> = {
    tenant_disabled: 'அமைப்புகளில் முடக்கப்பட்டது',
    opted_out: 'உறுப்பினர் தவிர்த்துள்ளார்',
    no_email_on_file: 'மின்னஞ்சல் இல்லை',
    no_subscription_context: 'சந்தா இல்லை',
    no_template_for_intent: 'வார்ப்புரு இல்லை',
    idempotency_replay: 'நகல்',
    quota_exceeded: 'தினசரி வரம்பு முடிந்தது',
  };
  // Cooldown reasons come as `cooldown_38s` — surface a friendlier phrasing.
  if (reason.startsWith('cooldown_')) {
    const secs = parseInt(reason.replace(/[^0-9]/g, ''), 10) || 0;
    const mins = Math.max(1, Math.ceil(secs / 60));
    return lang === 'ta' ? `${mins} நி இடைவெளி` : `Cool-down · ${mins}m`;
  }
  const map = lang === 'ta' ? ta : en;
  return map[reason] ?? reason;
}

// ─── Sub-components (kept local; not exported) ─────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }): ReactElement {
  return (
    <div
      style={{
        fontSize: 10,
        color: 'var(--ink-3)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontWeight: 600,
        marginBottom: 6,
      }}
    >
      {children}
    </div>
  );
}

function RecipientHeader({
  subscriber,
  loading,
  lang,
}: {
  subscriber: {
    name_en: string;
    name_ta: string;
    phone: string;
    email: string | null;
    initials: string;
  } | null;
  loading: boolean;
  lang: string;
}): ReactElement {
  const name =
    subscriber == null
      ? loading
        ? '…'
        : '?'
      : lang === 'ta'
        ? subscriber.name_ta || subscriber.name_en
        : subscriber.name_en;
  return (
    <div
      style={{
        padding: '12px 14px',
        borderBottom: '1px solid var(--line)',
        background: 'var(--bg-elev)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <div
        className="profile-ava"
        style={{
          width: 40,
          height: 40,
          fontSize: 14,
          // Keep .profile-ava's font/color, just override dimensions.
        }}
      >
        {subscriber?.initials ?? '··'}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className={lang === 'ta' ? 'tamil' : ''} style={{ fontWeight: 600, fontSize: 13 }}>
          {name}
        </div>
        <div
          className="mono"
          style={{ fontSize: 11, color: 'var(--ink-3)' }}
        >
          {subscriber?.phone ?? ''}
        </div>
        <div style={{ fontSize: 11, color: subscriber?.email ? 'var(--ink-3)' : 'var(--bad)' }}>
          {subscriber?.email
            ? subscriber.email
            : loading
              ? ''
              : lang === 'ta'
                ? 'மின்னஞ்சல் இல்லை'
                : 'No email on file'}
        </div>
      </div>
    </div>
  );
}

function PreviewCards({
  channels,
  skip_by_channel,
  data,
  lang,
}: {
  channels: Channel[];
  skip_by_channel: Map<Channel, string>;
  data: {
    wa_preview: string | null;
    wa_template_id: string | null;
    email_subject: string | null;
    email_text: string | null;
    voice_script: string | null;
    locale: 'ta' | 'ml' | 'kn' | 'te' | 'en';
  } | undefined;
  lang: string;
}): ReactElement {
  if (!data) return <PreviewLoading />;
  const live = channels.filter((c) => !skip_by_channel.has(c));
  if (live.length === 0) {
    return (
      <div style={{ fontSize: 12, color: 'var(--ink-3)', padding: 10 }}>
        {lang === 'ta' ? 'எந்த அலைவரிசையும் தேர்வு செய்யவில்லை' : 'Select at least one channel to preview.'}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {live.includes('whatsapp') && (
        <PreviewBlock
          icon="📱"
          label="WhatsApp"
          subtitle={
            data.wa_template_id
              ? `${data.wa_template_id} (${data.locale})`
              : lang === 'ta'
                ? 'நேரடி உரை'
                : 'Free-text send'
          }
        >
          {data.wa_preview || (lang === 'ta' ? '(வெற்று)' : '(empty)')}
        </PreviewBlock>
      )}
      {live.includes('email') && data.email_subject && (
        <PreviewBlock
          icon="📧"
          label="Email"
          subtitle={`"${data.email_subject}"`}
        >
          {data.email_text || (lang === 'ta' ? '(வெற்று)' : '(empty)')}
        </PreviewBlock>
      )}
      {live.includes('voice') && data.voice_script && (
        <PreviewBlock
          icon="🎙"
          label={lang === 'ta' ? 'குரல்' : 'Voice'}
          subtitle={lang === 'ta' ? 'TTS' : 'TTS script'}
        >
          {data.voice_script}
        </PreviewBlock>
      )}
    </div>
  );
}

function PreviewBlock({
  icon,
  label,
  subtitle,
  children,
}: {
  icon: string;
  label: string;
  subtitle: string;
  children: React.ReactNode;
}): ReactElement {
  return (
    <div
      style={{
        border: '1px solid var(--line)',
        borderRadius: 6,
        overflow: 'hidden',
        background: 'var(--surface)',
      }}
    >
      <div
        style={{
          padding: '6px 10px',
          background: 'var(--bg-elev)',
          borderBottom: '1px solid var(--line)',
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          fontSize: 11,
        }}
      >
        <span style={{ fontSize: 13 }}>{icon}</span>
        <span style={{ fontWeight: 600 }}>{label}</span>
        <span
          className="mono"
          style={{ color: 'var(--ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          · {subtitle}
        </span>
      </div>
      <div
        style={{
          padding: 10,
          fontSize: 12,
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          color: 'var(--ink)',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function PreviewLoading(): ReactElement {
  return (
    <div
      style={{
        padding: 18,
        textAlign: 'center',
        color: 'var(--ink-3)',
        fontSize: 12,
        border: '1px dashed var(--line)',
        borderRadius: 6,
      }}
    >
      …
    </div>
  );
}

function PreviewEmpty({ lang }: { lang: string }): ReactElement {
  return (
    <div
      style={{
        padding: 18,
        textAlign: 'center',
        color: 'var(--ink-3)',
        fontSize: 12,
        border: '1px dashed var(--line)',
        borderRadius: 6,
      }}
    >
      {lang === 'ta'
        ? 'செய்தியை தட்டச்சு செய்யுங்கள் — முன்னோட்டம் இங்கே தோன்றும்'
        : 'Type a message to see how it will be sent…'}
    </div>
  );
}

function PreviewError({ lang, message }: { lang: string; message: string }): ReactElement {
  return (
    <div
      style={{
        padding: 10,
        background: 'var(--bad-soft)',
        color: 'var(--bad)',
        fontSize: 12,
        border: '1px solid var(--bad)',
        borderRadius: 6,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        {lang === 'ta' ? 'முன்னோட்டத் தோல்வி' : "Couldn't render preview"}
      </div>
      <div style={{ fontSize: 11 }}>{message}</div>
    </div>
  );
}

function BulkPreview({
  count,
  intent_label,
  channels,
  lang,
}: {
  count: number;
  intent_label: string;
  channels: Channel[];
  lang: string;
}): ReactElement {
  const channel_names = channels
    .map((c) => CHANNEL_META.find((m) => m.id === c))
    .filter(Boolean)
    .map((m) => (lang === 'ta' ? m!.ta : m!.en))
    .join(' + ');
  return (
    <div
      style={{
        padding: 12,
        background: 'var(--bg-elev)',
        border: '1px solid var(--line)',
        borderRadius: 6,
        fontSize: 12,
        color: 'var(--ink)',
        lineHeight: 1.5,
      }}
    >
      {lang === 'ta'
        ? `${intent_label} செய்தி ${count} உறுப்பினர்களுக்கு ${channel_names} வழியாக.`
        : `${intent_label} message · ${count} members · via ${channel_names}.`}
      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 6 }}>
        {lang === 'ta'
          ? 'Opt-out + cooldown ஒவ்வொரு உறுப்பினருக்கும் சர்வரில் சரிபார்க்கப்படும்.'
          : 'Opt-out + cooldown checked per recipient on the server.'}
      </div>
    </div>
  );
}
